function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import { themeGet } from '@styled-system/theme-get';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import { Manager, Popper, Reference } from 'react-popper';
import styled from 'styled-components';
import { borderRadius, boxShadow, zIndex } from 'styled-system';
import Box from './Box';
import useOnClickOutside from './hooks/useOnClickOutside';
const ESCAPE_KEY = 27;
const StyledPopover = styled(Box).withConfig({
  displayName: "Popover__StyledPopover",
  componentId: "sc-qf7cx7-0"
})(["", " ", " ", " z-index:2000;"], borderRadius, boxShadow, zIndex);
const StyledPopoverArrow = styled.div.withConfig({
  displayName: "Popover__StyledPopoverArrow",
  componentId: "sc-qf7cx7-1"
})(["position:absolute;width:48px;height:48px;border-style:solid;&[data-placement*='top']{bottom:0;left:0;margin-bottom:-15px;width:48px;height:16px;border-width:16px 24px 0 24px;border-color:", " transparent transparent transparent;}&[data-placement*='right']{left:0;margin-left:-15px;height:48px;width:16px;border-width:24px 16px 24px 0;border-color:transparent ", " transparent transparent;}&[data-placement*='bottom']{top:0;left:0;margin-top:-15px;width:48px;height:16px;border-width:0 24px 16px 24px;border-color:transparent transparent ", " transparent;}&[data-placement*='left']{right:0;margin-right:-15px;height:48px;width:16px;border-width:24px 0 24px 16px;border-color:transparent transparent transparent ", ";}"], props => themeGet("colors.".concat(props.bg), props.bg), props => themeGet("colors.".concat(props.bg), props.bg), props => themeGet("colors.".concat(props.bg), props.bg), props => themeGet("colors.".concat(props.bg), props.bg));
const Popover = /*#__PURE__*/React.memo(_ref => {
  let {
    children,
    isOpen,
    modifiers,
    onClose,
    placement,
    referenceComponent,
    referenceComponentDisplay,
    ...props
  } = _ref;
  const containerRef = React.useRef(null);
  const popoverRef = React.useRef(null);
  const [inDom, setInDom] = React.useState(false);
  const ReferenceComponent = referenceComponent;
  const handleKeyDown = React.useCallback(event => {
    if (event.keyCode === ESCAPE_KEY) {
      event.preventDefault();
      onClose && onClose();
    }
  }, [onClose]);
  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
  React.useEffect(() => {
    containerRef.current = document.createElement('div');
    document.body.appendChild(containerRef.current);
    setInDom(true);
    return () => {
      document.body.removeChild(containerRef.current);
    };
  }, []);
  useOnClickOutside(popoverRef, onClose);
  return /*#__PURE__*/React.createElement(Manager, null, /*#__PURE__*/React.createElement(Reference, null, _ref2 => {
    let {
      ref
    } = _ref2;
    return /*#__PURE__*/React.createElement("div", {
      ref: ref,
      style: {
        display: referenceComponentDisplay
      }
    }, /*#__PURE__*/React.createElement(ReferenceComponent, null));
  }), inDom && isOpen && /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement("div", {
    ref: popoverRef
  }, /*#__PURE__*/React.createElement(Popper, {
    placement: placement,
    modifiers: {
      preventOverflow: {
        enabled: false
      },
      hide: {
        enabled: false
      },
      ...modifiers
    }
  }, _ref3 => {
    let {
      ref,
      style,
      placement,
      arrowProps
    } = _ref3;
    return /*#__PURE__*/React.createElement(StyledPopover, _extends({
      ref: ref,
      style: style,
      "data-placement": placement
    }, props), children, /*#__PURE__*/React.createElement(StyledPopoverArrow, {
      ref: arrowProps.ref,
      style: arrowProps.style,
      "data-placement": placement,
      bg: props.bg
    }));
  })), containerRef.current));
});
Popover.propTypes = {
  ...Box.propTypes,
  ...borderRadius.propTypes,
  modifiers: PropTypes.object,
  onClose: PropTypes.func,
  placement: PropTypes.string,
  referenceComponentDisplay: PropTypes.string
};
Popover.defaultProps = {
  bg: 'darkBlue',
  color: 'white',
  borderRadius: 'corner',
  py: 2,
  px: 3,
  modifiers: {},
  placement: 'auto',
  boxShadow: 'hover',
  referenceComponentDisplay: 'inline-block'
};
Popover.displayName = 'Popover';
export default Popover;